import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Footer from '../components/Footer';

const SERVICE_EXAMPLES = [
  'Bartender',
  'Server',
  'Barista',
  'Hair Stylist',
  'Food Truck Operator',
  'DJ',
  'Photo Booth Operator',
  'Valet',
  'Handyman',
  'Personal Trainer'
];

export default function SignupPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    serviceTitle: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password, displayName, serviceTitle } = formData;

    if (!email || !password || !displayName || !serviceTitle) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password, displayName, serviceTitle);
      toast.success('Account created! Let\'s set up your profile.');
      navigate('/onboarding/profile');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <div className="auth-container flex-1">
      <div className="auth-card animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-gold mb-2">Start Receiving Tips</h1>
          <p className="text-[#A1A1AA]">Create your free account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-xs uppercase tracking-widest text-[#A1A1AA]">Your Name</Label>
            <Input
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Tasha"
              className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B]"
              data-testid="signup-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="serviceTitle" className="text-xs uppercase tracking-widest text-[#A1A1AA]">What do you do?</Label>
            <Input
              id="serviceTitle"
              name="serviceTitle"
              value={formData.serviceTitle}
              onChange={handleChange}
              placeholder="Mobile Bartender"
              className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B]"
              data-testid="signup-service-input"
            />
            <p className="text-xs text-[#52525B]">
              Examples: {SERVICE_EXAMPLES.slice(0, 4).join(', ')}...
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#A1A1AA]">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B]"
              data-testid="signup-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#A1A1AA]">Password</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 6 characters"
                className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B] pr-10"
                data-testid="signup-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center justify-center gap-2 mt-6"
            data-testid="signup-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="text-xs text-[#52525B] mt-4 leading-relaxed text-center" data-testid="fee-disclosure-signup">
          Free to sign up. Golden Tips takes a 3% platform fee on tips.
          Opt into the{' '}
          <Link to="/terms#challenge-rules" className="text-[#F5C542]/90 hover:underline" data-testid="challenge-rules-link-signup">
            Top Earner Challenge
          </Link>{' '}
          for a shot at monthly prize pools (participants pay an additional 1%).
          By continuing you agree to our{' '}
          <Link to="/terms" className="text-[#F5C542]/90 hover:underline">Terms</Link>{' '}and{' '}
          <Link to="/privacy" className="text-[#F5C542]/90 hover:underline">Privacy Policy</Link>.
        </p>

        <p className="text-center mt-6 text-[#A1A1AA]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#F5C542] hover:underline font-medium" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
      </div>
      <Footer />
    </div>
  );
}
